/**
 * Who a chart is shared with, and changing that.
 *
 * Laid out in order of reach, widest first:
 *
 * 1. **Public** - one switch. On, the chart is in the Public catalog: everyone
 *    signed in can find, open and duplicate it. It is the `everyone -> viewer`
 *    grant (`PUT /charts/{id}/public`); the database refuses an everyone grant
 *    above viewer, so "public" can never quietly mean "everyone may edit".
 * 2. **People and groups** - one box to add someone, with the role and an
 *    optional end date beside it, then everyone who has access, each with a
 *    role menu: Viewer, Editor, or Remove access. One control per person, not a
 *    label plus a Revoke button.
 *
 * A grant is a row in `iam.role_binding` scoped to this chart, and the list is
 * re-read from the server after every change rather than patched locally: a
 * grant that looks applied but was refused is the one bug in a share dialog
 * nobody notices in time. `admin` is not offered, and the database would
 * refuse it anyway unless the actor is root - sharing a chart is not how anyone
 * should acquire authority over the app.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { ChartGrant, PrincipalSearch } from '../../api/types.ts';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { showToast } from '../../ui/toast.ts';
import { plotTypeIcon } from '../plot/plot-type-icons.ts';
import {
  catalogCharts,
  changeShareRole,
  chartById,
  chartGrants,
  closeShareDialog,
  findPrincipals,
  grantsLoading,
  revokeShare,
  setPublic,
  shareChart,
  sharingChartId,
} from './catalog-state.ts';
import { chartSummary } from './chart-summary.ts';
import { cachedThumbnail } from './thumbnail.ts';

type Role = 'viewer' | 'editor';

const EMPTY: PrincipalSearch = { users: [], groups: [] };

const ROLE_LABEL: Record<Role, string> = { viewer: 'Viewer', editor: 'Editor' };
const ROLE_HINT: Record<Role, string> = {
  viewer: 'Can open and duplicate',
  editor: 'Can also change and share it',
};

const GLOBE = svg`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8" />
  <path
    d="M3.5 12h17M12 3.5c2.6 2.4 3.8 5.2 3.8 8.5s-1.2 6.1-3.8 8.5c-2.6-2.4-3.8-5.2-3.8-8.5S9.4 5.9 12 3.5z"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linejoin="round"
  />
</svg>`;

const CLOCK = svg`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8" />
  <path d="M12 7.5V12l3 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

const LINK = svg`<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path
    d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
  />
</svg>`;

const CLOSE = svg`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
</svg>`;

/** "Maria Keller" → "MK". Two letters read at avatar size; three do not. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

@customElement('chart-share-dialog')
export class ChartShareDialog extends LightElement {
  private store = new StoreController(this, [sharingChartId, chartGrants, grantsLoading, catalogCharts]);

  @state() private query = '';
  @state() private found: PrincipalSearch = EMPTY;
  // Not `role`: that is a property of HTMLElement, and shadowing it breaks the
  // element's own typing.
  @state() private accessLevel: Role = 'viewer';
  @state() private useExpiry = false;
  @state() private expiresAt = '';
  @state() private busy = false;
  /** Which menu is open: the add box's role picker, or one grant's. */
  @state() private menu: 'add' | string | null = null;
  /**
   * Where that menu sits, relative to the dialog. Placed on the dialog rather
   * than inside the people list because the list scrolls, and a menu inside a
   * scroller is clipped by it - the last person's "Remove access" was cut off
   * at the list's edge. Not `position: fixed` either: the dialog is centred
   * with a transform, which makes it the containing block for fixed children.
   */
  @state() private menuAt: { x: number; y: number } | null = null;
  @state() private copied = false;

  private searchTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('click', this.onDocClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('click', this.onDocClick);
  }

  /** Escape closes the open menu first, then the dialog. */
  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !sharingChartId.value) return;
    if (this.menu) this.menu = null;
    else this.close();
  };

  private onDocClick = (): void => {
    if (this.menu) this.menu = null;
  };

  /** Open a role menu under its button, flipping up when there is no room below. */
  private openMenu(key: string, e: Event): void {
    e.stopPropagation();
    if (this.menu === key) {
      this.menu = null;
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const dialog = this.querySelector('.sd')?.getBoundingClientRect();
    if (!dialog) return;
    const width = 208;
    const height = key === 'add' ? 110 : 150;
    const below = r.bottom + 3 + height <= window.innerHeight;
    this.menuAt = {
      x: Math.max(8, r.right - width) - dialog.left,
      y: (below ? r.bottom + 3 : r.top - 3 - height) - dialog.top,
    };
    this.menu = key;
  }

  private close(): void {
    this.query = '';
    this.found = EMPTY;
    this.menu = null;
    this.useExpiry = false;
    this.expiresAt = '';
    this.accessLevel = 'viewer';
    closeShareDialog();
  }

  private onSearchInput(value: string): void {
    this.query = value;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    if (!value.trim()) {
      this.found = EMPTY;
      return;
    }
    // Debounced: the picker searches every user in the directory, and a
    // keystroke is not a question worth asking the database.
    this.searchTimer = window.setTimeout(async () => {
      this.found = await findPrincipals(value.trim());
    }, 250);
  }

  private async grant(principal: { user_id?: string; group_id?: string }): Promise<void> {
    const id = sharingChartId.value;
    if (!id || this.busy) return;
    this.busy = true;
    const expiry = this.useExpiry && this.expiresAt ? new Date(this.expiresAt).toISOString() : null;
    await shareChart(id, principal, this.accessLevel, expiry);
    this.busy = false;
    this.query = '';
    this.found = EMPTY;
  }

  private async run(work: () => Promise<unknown>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.menu = null;
    await work();
    this.busy = false;
  }

  private async copyLink(id: string): Promise<void> {
    const url = `${window.location.origin}/c/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 1400);
    } catch {
      showToast('error', 'Copy failed - the link is ' + url);
    }
  }

  /* ── pieces ─────────────────────────────────────────────── */

  private renderThumb(
    chartId: string,
    version: number | null,
    plotType: Parameters<typeof plotTypeIcon>[0],
  ): TemplateResult {
    const url = version != null ? cachedThumbnail(chartId, version) : null;
    return html`<span class="sd-thumb">
      ${url ? html`<img src=${url} alt="" />` : plotTypeIcon(plotType, 'sd-thumb-glyph')}
    </span>`;
  }

  private renderPublic(isPublic: boolean, id: string): TemplateResult {
    return html`<button
      class="sd-public ${isPublic ? 'is-on' : ''}"
      role="switch"
      aria-checked=${isPublic}
      ?disabled=${this.busy}
      @click=${() => void this.run(() => setPublic(id, !isPublic))}
    >
      <span class="sd-public-globe">${GLOBE}</span>
      <span class="sd-public-text">
        <strong>Public</strong>
        <span>
          ${isPublic
            ? 'Listed in the Public catalog. Everyone signed in can find, open and duplicate it.'
            : 'Only you and the people below can see it.'}
        </span>
      </span>
      <span class="sd-switch" aria-hidden="true"></span>
    </button>`;
  }

  private renderRoleMenu(current: Role, pick: (role: Role) => void, remove?: () => void): TemplateResult {
    const at = this.menuAt ?? { x: 0, y: 0 };
    return html`<div
      class="sd-menu"
      role="menu"
      style=${`left:${at.x}px; top:${at.y}px`}
      @click=${(e: Event) => e.stopPropagation()}
    >
      ${(['viewer', 'editor'] as Role[]).map(
        (role) => html`<button
          class="sd-menu-item ${role === current ? 'is-current' : ''}"
          role="menuitemradio"
          aria-checked=${role === current}
          @click=${() => pick(role)}
        >
          ${ROLE_LABEL[role]}<small>${ROLE_HINT[role]}</small>
        </button>`,
      )}
      ${remove
        ? html`<div class="sd-menu-sep"></div>
            <button class="sd-menu-item is-danger" role="menuitem" @click=${remove}>
              Remove access
            </button>`
        : nothing}
    </div>`;
  }

  private renderCandidates(): TemplateResult | typeof nothing {
    if (!this.query.trim()) return nothing;
    const has = (pid: string) => chartGrants.value.some((g) => g.principal_id === pid);
    const people = this.found.users.map((u) => ({
      id: u.id,
      name: u.display_name,
      sub: u.email ?? 'person',
      group: false,
      principal: { user_id: u.id },
    }));
    const groups = this.found.groups.map((g) => ({
      id: g.id,
      name: g.name,
      sub: 'group',
      group: true,
      principal: { group_id: g.id },
    }));
    const all = [...people, ...groups];
    return html`<div class="sd-pop" role="listbox">
      ${all.map(
        (p) => html`<button
          class="sd-candidate"
          role="option"
          ?disabled=${has(p.id) || this.busy}
          @mousedown=${(e: Event) => {
            // Before the input's blur, so the click lands.
            e.preventDefault();
            if (!has(p.id)) void this.grant(p.principal);
          }}
        >
          <span class="sd-avatar ${p.group ? 'is-group' : ''}">${initials(p.name)}</span>
          <span class="sd-who">
            <span class="sd-name">${p.name}</span>
            <span class="sd-sub">${has(p.id) ? 'already has access' : p.sub}</span>
          </span>
          <span class="sd-candidate-role">${has(p.id) ? '' : `Add as ${ROLE_LABEL[this.accessLevel]}`}</span>
        </button>`,
      )}
      ${!all.length ? html`<p class="sd-none">Nobody matches “${this.query}”</p>` : nothing}
    </div>`;
  }

  private renderGrant(grant: ChartGrant, chartId: string): TemplateResult {
    const role = grant.role as Role;
    const isGroup = grant.principal_kind === 'group';
    return html`<li class="sd-row">
      <span class="sd-avatar ${isGroup ? 'is-group' : ''}">${initials(grant.principal_name)}</span>
      <span class="sd-who">
        <span class="sd-name">${grant.principal_name}</span>
        <span class="sd-sub">
          ${isGroup ? 'group' : (grant.principal_email ?? 'person')}
          ${grant.expires_at ? html` · <span class="sd-until">until ${shortDate(grant.expires_at)}</span>` : nothing}
        </span>
      </span>
      <span class="sd-rolepick">
        <button
          class="sd-role"
          aria-haspopup="menu"
          aria-expanded=${this.menu === grant.id}
          ?disabled=${this.busy}
          @click=${(e: Event) => this.openMenu(grant.id, e)}
        >
          ${ROLE_LABEL[role] ?? grant.role}<span class="sd-caret" aria-hidden="true">▾</span>
        </button>
      </span>
    </li>`;
  }

  /** The one open role menu, drawn on the dialog itself - see `menuAt`. */
  private renderOpenMenu(chartId: string): TemplateResult | typeof nothing {
    if (!this.menu) return nothing;
    if (this.menu === 'add') {
      return this.renderRoleMenu(this.accessLevel, (role) => {
        this.accessLevel = role;
        this.menu = null;
      });
    }
    const grant = chartGrants.value.find((g) => g.id === this.menu);
    if (!grant) return nothing;
    const role = grant.role as Role;
    return this.renderRoleMenu(
      role,
      (next) =>
        void this.run(async () => {
          if (next !== role) await changeShareRole(chartId, grant.id, next);
        }),
      () => void this.run(() => revokeShare(chartId, grant.id)),
    );
  }

  override render(): TemplateResult | typeof nothing {
    const id = sharingChartId.value;
    if (!id) return nothing;
    const chart = chartById(id);
    if (!chart) return nothing;
    const people = chartGrants.value.filter((g) => g.principal_kind !== 'everyone');
    const ownerIsMe = chart.is_owner;
    return html`
      <div class="share-scrim" @click=${() => this.close()}></div>
      <div class="share-dialog sd" role="dialog" aria-modal="true" aria-labelledby="sd-title">
        <header class="sd-head">
          ${this.renderThumb(chart.id, chart.thumbnail_version, chart.plot_type)}
          <span class="sd-title">
            <span id="sd-title" class="sd-title-name">${chart.title}</span>
            <span class="sd-title-meta">${chartSummary(chart)}</span>
          </span>
          <button class="sd-x" title="Close" aria-label="Close" @click=${() => this.close()}>
            ${CLOSE}
          </button>
        </header>

        ${this.renderPublic(chart.is_public, id)}

        <section class="sd-section" aria-label="People and groups">
          <span class="sd-label">People and groups</span>
          <div class="sd-add">
            <input
              type="search"
              placeholder="Add people or groups…"
              aria-label="Add people or groups"
              autocomplete="off"
              .value=${this.query}
              @input=${(e: Event) => this.onSearchInput((e.target as HTMLInputElement).value)}
            />
            <button
              class="sd-add-icon ${this.useExpiry ? 'is-on' : ''}"
              title=${this.useExpiry ? 'No end date' : 'Set an end date for the access you give'}
              aria-label="Access ends"
              aria-pressed=${this.useExpiry}
              @click=${() => {
                this.useExpiry = !this.useExpiry;
              }}
            >
              ${CLOCK}
            </button>
            <span class="sd-rolepick">
              <button
                class="sd-add-role"
                aria-haspopup="menu"
                aria-expanded=${this.menu === 'add'}
                @click=${(e: Event) => this.openMenu('add', e)}
              >
                ${ROLE_LABEL[this.accessLevel]}<span class="sd-caret" aria-hidden="true">▾</span>
              </button>
            </span>
            ${this.renderCandidates()}
          </div>
          ${this.useExpiry
            ? html`<label class="sd-expiry">
                Access ends
                <input
                  type="date"
                  .value=${this.expiresAt}
                  @change=${(e: Event) => {
                    this.expiresAt = (e.target as HTMLInputElement).value;
                  }}
                />
                <span class="sd-expiry-note">for people you add now</span>
              </label>`
            : nothing}

          <ul
            class="sd-rows"
            @scroll=${() => {
              if (this.menu) this.menu = null;
            }}
          >
            <li class="sd-row">
              <span class="sd-avatar">${initials(chart.owner.display_name)}</span>
              <span class="sd-who">
                <span class="sd-name">${ownerIsMe ? 'You' : chart.owner.display_name}</span>
                <span class="sd-sub">${chart.owner.email ?? ''}</span>
              </span>
              <span class="sd-role-static">Owner</span>
            </li>
            ${chart.is_public
              ? html`<li class="sd-row">
                  <span class="sd-avatar is-everyone">${GLOBE}</span>
                  <span class="sd-who">
                    <span class="sd-name">Everyone signed in</span>
                    <span class="sd-sub">through the Public catalog</span>
                  </span>
                  <span class="sd-role-static">Viewer</span>
                </li>`
              : nothing}
            ${grantsLoading.value
              ? html`<li class="sd-row sd-loading">Loading…</li>`
              : people.map((g) => this.renderGrant(g, id))}
          </ul>
        </section>

        <footer class="sd-foot">
          <button class="sd-ghost ${this.copied ? 'is-ok' : ''}" @click=${() => void this.copyLink(id)}>
            ${LINK} ${this.copied ? 'Copied' : 'Copy link'}
          </button>
          <button class="catalog-cta" @click=${() => this.close()}>Done</button>
        </footer>
        ${this.renderOpenMenu(id)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chart-share-dialog': ChartShareDialog;
  }
}
